import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
// Removed unused CustomSelect import
import AccountSelection from '../components/AccountSelection';
import SymbolSelection from '../components/SymbolSelection';
import TradeControls from '../components/TradeControls';
import RiskManagement from '../components/RiskManagement';
import TradeExecution from '../components/TradeExecution';
import PartialProfitTargets from '../components/PartialProfitTargets';
import TradeCalculations from '../components/TradeCalculations';
import JournalModal from '../components/JournalModal';
import { Accordion, AccordionItem } from '../components/ui/accordion';
import TradingChart from '../components/TradingChart';
import PriceFeedProvider, { usePriceFeed } from '../context/PriceFeedContext';
import { useAccountData } from '../context/AccountDataProvider';
import { getSymbolInfo } from '../api/api';
import { setDigitsFor } from '../utils/symbolDigitsStore';
import { useTradeState } from '../hooks/useTradeState';
import { useTrade } from '../context/TradeProvider';
import { Button } from '../components/ui/button';
import { Maximize2, Minimize2, MousePointer2, Plus, X } from 'lucide-react';

const timeframes = [
    { label: '1M', value: 'M1' },
    { label: '5M', value: 'M5' },
    { label: '15M', value: 'M15' },
    { label: '30M', value: 'M30' },
    { label: '1H', value: 'H1' },
    { label: '4H', value: 'H4' },
    { label: '1D', value: 'D1' },
    { label: '1W', value: 'W1' },
    { label: '1MN', value: 'MN1' },
];

const createChartId = () => `chart-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const createChartConfig = (symbol = 'EURUSD', timeframe = 'M15') => ({
    id: createChartId(),
    symbol: symbol || 'EURUSD',
    timeframe: timeframe || 'M15',
});

const LiveTradeView = ({ watchlist: initialWatchlist }) => {
    const { isLoggingEnabled, toggleLogging } = usePriceFeed();
    const { accountId, symbol, timeframe, setTradeContext } = useTrade();
    const {
        tradeDirection,
        setTradeDirection,
        orderType,
        setOrderType,
        stopLossType,
        setStopLossType,
        riskPercent,
        setRiskPercent,
        atrMultiplier,
        setAtrMultiplier,
        tpMode,
        setTpMode,
        rrRatio,
        setRrRatio,
        takeProfitValue,
        setTakeProfitValue,
    } = useTradeState();
    const {
        balance: accountBalance,
        equity: accountEquity,
        freeMargin,
        cashAvailable,
        currency: accountCurrency,
        connect,
    } = useAccountData();
    const { bid, ask } = usePriceFeed();
    const [pipSize, setPipSize] = useState(null);
    const [tickSize, setTickSize] = useState(null);
    const [contractSize, setContractSize] = useState(null);
    const [symbolCurrency, setSymbolCurrency] = useState(null);
    const [priceConversionRate, setPriceConversionRate] = useState(null);
    const [digits, setDigits] = useState(undefined);
    const [lotStep, setLotStep] = useState(0.01);
    const [minLot, setMinLot] = useState(undefined);
    const [maxLot, setMaxLot] = useState(undefined);
    const [lotSize, setLotSize] = useState('N/A');
    const [stopLossPrice, setStopLossPrice] = useState('N/A');
    const [stopLossDistance, setStopLossDistance] = useState('N/A');
    const [finalStopLossDistance, setFinalStopLossDistance] = useState(0);
    const [projectedProfit, setProjectedProfit] = useState(0);
    const [projectedLoss, setProjectedLoss] = useState(0);
    const [takeProfitPrice, setTakeProfitPrice] = useState('');
    const [reason, setReason] = useState('');
    const [limitEntryPrice, setLimitEntryPrice] = useState('');
    const [profitMode, setProfitMode] = useState('single');
    const [targets, setTargets] = useState([
        { rank: 1, share: 0.5, tp_type: 'RR', rr: 1.5, price: '', atr_multiplier: 1 },
        { rank: 2, share: 0.5, tp_type: 'RR', rr: 3.0, price: '', atr_multiplier: 1 },
    ]);
    const [watchlist, setWatchlist] = useState(initialWatchlist || []);
    const [executedTradeId, setExecutedTradeId] = useState(null);
    const [showJournalModal, setShowJournalModal] = useState(false);
    const [showRecentTrades, setShowRecentTrades] = useState(false);
    const [charts, setCharts] = useState(() => [createChartConfig(symbol, timeframe)]);
    const [activeChartId, setActiveChartId] = useState(null);
    const [fullscreenChartId, setFullscreenChartId] = useState(null);
    const chartPanelRefs = useRef(new Map());

    const activeChart = useMemo(
        () => charts.find((chart) => chart.id === activeChartId) || charts[0] || null,
        [charts, activeChartId]
    );

    const setChartPanelRef = useCallback((chartId, node) => {
        if (!chartId) return;
        if (node) {
            chartPanelRefs.current.set(chartId, node);
        } else {
            chartPanelRefs.current.delete(chartId);
        }
    }, []);

    useEffect(() => {
        if (!activeChartId && charts[0]) {
            setActiveChartId(charts[0].id);
        }
    }, [activeChartId, charts]);

    useEffect(() => {
        if (!activeChart?.id) return;
        const nextSymbol = symbol || activeChart.symbol || 'EURUSD';
        const nextTimeframe = timeframe || activeChart.timeframe || 'M15';

        setCharts((prevCharts) => {
            let changed = false;
            const nextCharts = prevCharts.map((chart) => {
                if (chart.id !== activeChart.id) return chart;
                if (chart.symbol === nextSymbol && chart.timeframe === nextTimeframe) return chart;
                changed = true;
                return { ...chart, symbol: nextSymbol, timeframe: nextTimeframe };
            });
            return changed ? nextCharts : prevCharts;
        });
    }, [symbol, timeframe, activeChart?.id, activeChart?.symbol, activeChart?.timeframe]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            const currentElement = document.fullscreenElement;
            const entry = Array.from(chartPanelRefs.current.entries()).find(([, node]) => node === currentElement);
            setFullscreenChartId(entry?.[0] || null);
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
    }, []);

    const activateChart = useCallback((chart) => {
        if (!chart) return;
        setActiveChartId(chart.id);
        setTradeContext({ symbol: chart.symbol, timeframe: chart.timeframe });
    }, [setTradeContext]);

    const handleAddChart = useCallback(() => {
        const base = activeChart || { symbol, timeframe };
        const nextChart = createChartConfig(base.symbol, base.timeframe);
        setCharts((prevCharts) => [...prevCharts, nextChart]);
        setActiveChartId(nextChart.id);
        setTradeContext({ symbol: nextChart.symbol, timeframe: nextChart.timeframe });
    }, [activeChart, symbol, timeframe, setTradeContext]);

    const handleCloseChart = useCallback((chartId) => {
        setCharts((prevCharts) => {
            if (prevCharts.length <= 1) return prevCharts;
            const closingIndex = prevCharts.findIndex((chart) => chart.id === chartId);
            const nextCharts = prevCharts.filter((chart) => chart.id !== chartId);

            if (chartId === activeChartId) {
                const replacement = nextCharts[Math.max(0, closingIndex - 1)] || nextCharts[0];
                if (replacement) {
                    setActiveChartId(replacement.id);
                    setTradeContext({ symbol: replacement.symbol, timeframe: replacement.timeframe });
                }
            }

            return nextCharts;
        });
    }, [activeChartId, setTradeContext]);

    const handleChartTimeframeChange = useCallback((chartId, nextTimeframe) => {
        if (!nextTimeframe) return;
        setCharts((prevCharts) => prevCharts.map((chart) => (
            chart.id === chartId ? { ...chart, timeframe: nextTimeframe } : chart
        )));
        if (chartId === activeChartId) {
            setTradeContext({ timeframe: nextTimeframe });
        }
    }, [activeChartId, setTradeContext]);

    const toggleChartFullscreen = useCallback(async (chartId) => {
        const node = chartPanelRefs.current.get(chartId);
        if (!node || !document.fullscreenEnabled) return;

        try {
            if (document.fullscreenElement === node) {
                await document.exitFullscreen();
                return;
            }
            if (document.fullscreenElement) {
                await document.exitFullscreen();
            }
            await node.requestFullscreen();
            setFullscreenChartId(chartId);
            requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
        } catch (error) {
            console.error('Failed to toggle chart fullscreen:', error);
        }
    }, []);

    const resetTradeTerminal = useCallback(() => {
        setLotSize('N/A');
        setStopLossPrice('N/A');
        setStopLossDistance('N/A');
        setFinalStopLossDistance(0);
        setProjectedProfit(0);
        setProjectedLoss(0);
        setTakeProfitPrice('');
        setLimitEntryPrice('');
        setReason('');
    }, []);

    useEffect(() => {
        setWatchlist(initialWatchlist || []);
    }, [initialWatchlist]);

    useEffect(() => {
        if (accountId) {
            connect(accountId);
        }
        // No disconnect on cleanup — connection persists across page navigation
    }, [accountId, connect]);

    useEffect(() => {
        // This effect now only fetches symbol info when a *valid* symbol is set in the context.
        // The validation itself is handled in SymbolSelection.
        if (accountId && symbol) {
            getSymbolInfo(accountId, symbol)
                .then((symbolInfo) => {
                    setPipSize(symbolInfo.pip_size);
                    setTickSize(symbolInfo.tick_size);
                    setContractSize(symbolInfo.contract_size);
                    setSymbolCurrency(symbolInfo.currency ?? null);
                    const conversionRate = Number(symbolInfo.price_conversion_rate);
                    setPriceConversionRate(Number.isFinite(conversionRate) ? conversionRate : null);
                    // Lot step and bounds for proper lot rounding
                    const step = Number(symbolInfo.lot_step ?? symbolInfo.volume_step ?? 0.01) || 0.01;
                    setLotStep(step);
                    setMinLot(typeof symbolInfo.min_lot === 'number' ? symbolInfo.min_lot : (typeof symbolInfo.min_volume === 'number' ? symbolInfo.min_volume : undefined));
                    setMaxLot(typeof symbolInfo.max_lot === 'number' ? symbolInfo.max_lot : (typeof symbolInfo.max_volume === 'number' ? symbolInfo.max_volume : undefined));
                    if (typeof symbolInfo.digits === 'number') {
                        setDigits(symbolInfo.digits);
                        setDigitsFor(accountId, symbol, symbolInfo.digits);
                    } else {
                        setDigits(undefined);
                    }
                    resetTradeTerminal();
                })
                .catch((error) => {
                    // It's unlikely to fail here now, but good to have a fallback.
                    console.error("Error fetching info for a supposedly valid symbol:", error);
                });
        } else {
            // If there's no symbol, clear the info.
            setPipSize(null);
            setTickSize(null);
            setContractSize(null);
            setSymbolCurrency(null);
            setPriceConversionRate(null);
            setDigits(undefined);
            setLotStep(0.01);
            setMinLot(undefined);
            setMaxLot(undefined);
        }
    }, [accountId, symbol, resetTradeTerminal]);

    useEffect(() => {
        setFinalStopLossDistance(stopLossDistance);
    }, [stopLossDistance]);

    const handleTargetsCalculated = (calculatedTargets) => {
        const newTargets = targets.map(originalTarget => {
            const foundCalculated = calculatedTargets.find(ct => ct.rank === originalTarget.rank);
            if (foundCalculated && foundCalculated.calculatedPrice !== undefined) {
                return { ...originalTarget, price: foundCalculated.calculatedPrice };
            }
            return originalTarget;
        });
        setTargets(newTargets);
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-4 p-2 sm:p-4 min-h-screen bg-background">
            {/* Left Column: Charts */}
            <div className={`lg:col-span-8 flex flex-col order-1 lg:order-1 min-h-0 ${charts.length > 1 ? 'h-auto min-h-[50vh] lg:h-full' : 'h-[50vh] lg:h-full'}`}>
                <Card className="flex-grow flex flex-col min-h-0">
                    <CardHeader className="flex flex-col sm:flex-row sm:items-center gap-2 p-2 sm:p-6">
                        <div className="flex flex-wrap items-center gap-2 sm:gap-4 w-full">
                            <div className="flex items-center gap-1 sm:gap-2">
                                <span className="text-sm sm:text-lg font-semibold">Account:</span>
                                <AccountSelection
                                    selectedAccount={accountId}
                                    setSelectedAccount={(newAccountId) => setTradeContext({ accountId: newAccountId })}
                                    setRiskPercentFromSettings={setRiskPercent}
                                    displayMode="compact"
                                />
                            </div>
                            <div className="flex items-center">
                                <SymbolSelection
                                    selectedSymbol={symbol}
                                    setSelectedSymbol={(newSymbol) => setTradeContext({ symbol: newSymbol })}
                                    watchlist={watchlist}
                                    accountId={accountId}
                                />
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 gap-1 px-2"
                                onClick={handleAddChart}
                                title="Open another chart"
                            >
                                <Plus className="h-4 w-4" />
                                <span className="hidden sm:inline">Chart</span>
                            </Button>
                            {/* Removed header timeframe select; moved into chart overlay */}
                            <button
                                onClick={toggleLogging}
                                className={`ml-auto p-1.5 sm:p-2 rounded text-xs sm:text-sm ${isLoggingEnabled ? 'bg-green-500' : 'bg-red-500'} text-white`}
                            >
                                {isLoggingEnabled ? 'Log ON' : 'Log OFF'}
                            </button>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col min-h-0 p-1 sm:p-2">
                        <div className={`grid flex-1 min-h-0 gap-2 ${charts.length > 1 ? 'grid-cols-1 xl:grid-cols-2' : 'grid-cols-1'}`}>
                            {charts.map((chart) => {
                                const isActive = chart.id === activeChart?.id;
                                const isFullscreen = chart.id === fullscreenChartId;
                                const chartContent = (
                                    <TradingChart
                                        selectedAccount={accountId}
                                        selectedSymbol={chart.symbol}
                                        selectedTimeframe={chart.timeframe}
                                        stopLossPrice={isActive ? stopLossPrice : undefined}
                                        takeProfitPrice={isActive ? takeProfitPrice : undefined}
                                        tickSize={isActive ? tickSize : undefined}
                                        digits={isActive ? digits : undefined}
                                        pipSize={isActive ? pipSize : undefined}
                                        showTimeframeOverlay={true}
                                        timeframeOptions={timeframes}
                                        onTimeframeChange={(newTimeframe) => handleChartTimeframeChange(chart.id, newTimeframe)}
                                        showRecentTrades={showRecentTrades}
                                        onToggleRecentTrades={() => setShowRecentTrades(v => !v)}
                                    />
                                );

                                return (
                                    <div
                                        key={chart.id}
                                        ref={(node) => setChartPanelRef(chart.id, node)}
                                        className={[
                                            'flex min-h-[340px] flex-col overflow-hidden rounded-md border bg-background',
                                            charts.length === 1 ? 'h-full min-h-0' : '',
                                            isFullscreen ? 'h-screen' : '',
                                            isActive ? 'border-primary/80 shadow-[0_0_0_1px_rgba(59,130,246,0.35)]' : 'border-borderDefault',
                                        ].filter(Boolean).join(' ')}
                                        onMouseDown={() => activateChart(chart)}
                                    >
                                        <div className="flex h-9 shrink-0 items-center gap-2 border-b border-borderDefault bg-backgroundSurface/80 px-2">
                                            <button
                                                type="button"
                                                className="flex min-w-0 flex-1 items-center gap-2 text-left"
                                                onClick={() => activateChart(chart)}
                                                title="Make this chart active for the trade panel"
                                            >
                                                <span className={`h-2 w-2 shrink-0 rounded-full ${isActive ? 'bg-primary' : 'bg-textSecondary/50'}`} />
                                                <span className="truncate text-xs font-semibold text-textPrimary">{chart.symbol}</span>
                                                <span className="rounded border border-borderDefault px-1.5 py-0.5 text-[10px] font-semibold text-textSecondary">
                                                    {chart.timeframe}
                                                </span>
                                            </button>
                                            {!isActive && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0"
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        activateChart(chart);
                                                    }}
                                                    title="Use this chart for the trade panel"
                                                >
                                                    <MousePointer2 className="h-4 w-4" />
                                                </Button>
                                            )}
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="h-7 w-7 p-0"
                                                onMouseDown={(event) => event.stopPropagation()}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    toggleChartFullscreen(chart.id);
                                                }}
                                                title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                                            >
                                                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                                            </Button>
                                            {charts.length > 1 && (
                                                <Button
                                                    type="button"
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                                                    onMouseDown={(event) => event.stopPropagation()}
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        handleCloseChart(chart.id);
                                                    }}
                                                    title="Close chart"
                                                >
                                                    <X className="h-4 w-4" />
                                                </Button>
                                            )}
                                        </div>
                                        <div className="min-h-0 flex-1">
                                            {isActive ? chartContent : (
                                                <PriceFeedProvider accountId={accountId} symbol={chart.symbol} timeframe={chart.timeframe}>
                                                    {chartContent}
                                                </PriceFeedProvider>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Right Column: Trade Terminal */}
            <div className="lg:col-span-4 flex flex-col h-auto lg:h-full order-2 lg:order-2">
                <Card className="flex-grow flex flex-col">
                    <CardHeader className="p-3 sm:p-6">
                        <CardTitle className="text-base sm:text-xl">Trade Terminal</CardTitle>
                        <div className="mt-2 text-sm sm:text-xl font-bold text-textPrimary grid grid-cols-2 sm:block gap-2">
                            <p>Balance: ${typeof accountBalance === 'number' ? accountBalance.toFixed(2) : "..."}</p>
                            <p>Equity: ${typeof accountEquity === 'number' ? accountEquity.toFixed(2) : "..."}</p>
                        </div>
                    </CardHeader>
                    <CardContent className="flex-grow overflow-y-auto space-y-3 sm:space-y-4 p-3 sm:p-6">
                        <div>
                            <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Trade Controls</h3>
                            <TradeControls
                                tradeDirection={tradeDirection}
                                setTradeDirection={setTradeDirection}
                                selectedAccount={accountId}
                                selectedSymbol={symbol}
                                orderType={orderType}
                                setOrderType={setOrderType}
                                limitEntryPrice={limitEntryPrice}
                                setLimitEntryPrice={setLimitEntryPrice}
                            />
                        </div>
                        <Accordion>
                            <AccordionItem title="Symbol Info">
                                <div className="space-y-1">
                                    <div className="flex flex-wrap gap-2 sm:gap-4 text-xs">
                                        <p>Pip: {pipSize}</p>
                                        <p>Tick: {tickSize}</p>
                                        <p>Contract: {contractSize}</p>
                                    </div>
                                </div>
                            </AccordionItem>
                        </Accordion>
                        <div>
                            <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Risk Management</h3>
                            <RiskManagement
                                stopLossType={stopLossType}
                                setStopLossType={setStopLossType}
                                stopLossDistance={stopLossDistance}
                                setStopLossDistance={setStopLossDistance}
                                stopLossPrice={stopLossPrice}
                                setStopLossPrice={setStopLossPrice}
                                riskPercent={riskPercent}
                                setRiskPercent={setRiskPercent}
                                tradeDirection={tradeDirection}
                                bidPrice={bid}
                                askPrice={ask}
                                pipSize={pipSize}
                                tickSize={tickSize}
                                contractSize={contractSize}
                                lotStep={lotStep}
                                minLot={minLot}
                                maxLot={maxLot}
                                setLotSize={setLotSize}
                                accountEquity={accountEquity}
                                freeMargin={freeMargin}
                                cashAvailable={cashAvailable}
                                accountCurrency={accountCurrency}
                                symbolCurrency={symbolCurrency}
                                priceConversionRate={priceConversionRate}
                                takeProfitValue={takeProfitValue}
                                setTakeProfitValue={setTakeProfitValue}
                                rrRatio={rrRatio}
                                setRrRatio={setRrRatio}
                                setProjectedProfit={setProjectedProfit}
                                projectedProfit={projectedProfit}
                                setProjectedLoss={setProjectedLoss}
                                projectedLoss={projectedLoss}
                                tpMode={tpMode}
                                setTpMode={setTpMode}
                                takeProfitPrice={takeProfitPrice}
                                setTakeProfitPrice={setTakeProfitPrice}
                                selectedSymbol={symbol}
                                selectedTimeframe={timeframe}
                                atrMultiplier={atrMultiplier}
                                setAtrMultiplier={setAtrMultiplier}
                                profitMode={profitMode}
                                setProfitMode={setProfitMode}
                                targets={targets}
                                onTargetsCalculated={handleTargetsCalculated}
                                orderType={orderType}
                                limitEntryPrice={limitEntryPrice}
                            />
                        </div>
                        {profitMode === 'multi' && (
                            <div>
                                <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Partial Profit Targets</h3>
                                <PartialProfitTargets
                                    accountId={accountId}
                                    targets={targets}
                                    setTargets={setTargets}
                                />
                            </div>
                        )}
                        <div>
                            <h3 className="text-base sm:text-xl font-bold mb-2 text-textPrimary">Trade Calculations</h3>
                            <TradeCalculations
                                lotSize={lotSize}
                                projectedLoss={projectedLoss}
                                projectedProfit={projectedProfit}
                                profitTarget={takeProfitPrice}
                                stopLossPrice={stopLossPrice}
                                stopLossDistance={stopLossDistance}
                            />
                        </div>
                        <div className="mt-3 sm:mt-4">
                            <TradeExecution
                                selectedAccount={accountId}
                                selectedSymbol={symbol}
                                tradeDirection={tradeDirection}
                                lotSize={lotSize}
                                bidPrice={bid}
                                askPrice={ask}
                                stopLossDistance={finalStopLossDistance}
                                pipSize={pipSize}
                                riskPercent={riskPercent}
                                rrRatio={rrRatio}
                                projectedProfit={projectedProfit}
                                projectedLoss={projectedLoss}
                                takeProfitValue={tpMode === "price" ? takeProfitPrice : takeProfitValue}
                                tpMode={tpMode}
                                takeProfitPrice={takeProfitPrice}
                                reason={reason}
                                orderType={orderType}
                                limitEntryPrice={limitEntryPrice}
                                profitMode={profitMode}
                                targets={targets}
                                lotStep={lotStep}
                                minLot={minLot}
                                maxLot={maxLot}
                                setExecutedTradeId={setExecutedTradeId}
                                setShowJournalModal={setShowJournalModal}
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
            {showJournalModal && (
                <JournalModal
                    show={showJournalModal}
                    onClose={() => {
                        setShowJournalModal(false);
                        setExecutedTradeId(null); // Clear the trade ID when modal is closed
                    }}
                    tradeId={executedTradeId}
                />
            )}
        </div>
    );
};

const LiveTradePage = ({ watchlist }) => (
    <LiveTradeView watchlist={watchlist} />
);

export default LiveTradePage;
